import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  TablePagination,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { useGetVanTripsQuery, useGetVansQuery } from '../../../store/api/vanSalesApi';
import { usePermission } from '../../../hooks/usePermission';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const STATUS_COLORS = {
  draft: 'default',
  loaded: 'info',
  in_progress: 'warning',
  completed: 'success',
  cancelled: 'error',
};

const STATUS_LABELS = {
  draft: 'Draft',
  loaded: 'Loaded',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

function VanTripsList() {
  const navigate = useNavigate();
  const { isAdmin } = usePermission();
  const getStartOfMonth = () => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  };
  const getToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  };
  const toApiDate = (d) => {
    if (!d) return undefined;
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return undefined;
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
      date.getDate()
    ).padStart(2, '0')}`;
  };

  const [filters, setFilters] = useState({
    status: '',
    vanId: '',
    fromDate: getStartOfMonth(),
    toDate: getToday(),
    page: 1,
    limit: 10
  });

  const apiFilters = {
    ...filters,
    fromDate: toApiDate(filters.fromDate),
    toDate: toApiDate(filters.toDate),
  };

  const { data, isLoading } = useGetVanTripsQuery(apiFilters);
  const trips = data?.trips || [];
  const pagination = data?.pagination;
  const { data: vans = [] } = useGetVansQuery();

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Van Sales - Trips
          </Typography>
          {!isAdmin && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Showing your trips, loads, sales, unloads and reports only.
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<DirectionsCarIcon />}
            onClick={() => navigate('/van-sales/vans')}
          >
            Manage Vans
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/van-sales/trips/new')}
          >
            New Trip
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            select
            size="small"
            label="Status"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All</MenuItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Van"
            value={filters.vanId}
            onChange={(e) => setFilters({ ...filters, vanId: e.target.value, page: 1 })}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All Vans</MenuItem>
            {vans.map((v) => (
              <MenuItem key={v.id} value={v.id}>
                {v.name} ({v.plateNumber})
              </MenuItem>
            ))}
          </TextField>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="From"
              value={filters.fromDate}
              onChange={(newValue) => setFilters({ ...filters, fromDate: newValue, page: 1 })}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
            />
            <DatePicker
              label="To"
              value={filters.toDate}
              onChange={(newValue) => setFilters({ ...filters, toDate: newValue, page: 1 })}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
            />
          </LocalizationProvider>
        </Box>
      </Paper>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600 }}>Trip #</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Van</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Salesperson</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Warehouse</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Load Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Return Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    Loading...
                  </TableCell>
                </TableRow>
              ) : trips.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No trips found. Create a new trip to get started.
                  </TableCell>
                </TableRow>
              ) : (
                trips.map((trip) => (
                  <TableRow key={trip.id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{trip.tripNumber}</TableCell>
                    <TableCell>
                      {trip.van?.name} ({trip.van?.plateNumber})
                    </TableCell>
                    <TableCell>
                      {trip.salesperson
                        ? `${trip.salesperson.firstName} ${trip.salesperson.lastName}`
                        : '—'}
                    </TableCell>
                    <TableCell>{trip.warehouse?.name || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABELS[trip.status] || trip.status}
                        color={STATUS_COLORS[trip.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(trip.loadDate)}</TableCell>
                    <TableCell>{formatDate(trip.returnDate)}</TableCell>
                    <TableCell>
                      <Tooltip title="View Trip">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/van-sales/trips/${trip.id}`)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {pagination && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {`Page ${pagination.page} of ${pagination.pages || 1} (${pagination.total} total)`}
            </Typography>
            <TablePagination
              component="div"
              count={pagination.total}
              page={(pagination.page || 1) - 1}
              onPageChange={(_, newPage) =>
                setFilters((prev) => ({
                  ...prev,
                  page: newPage + 1,
                }))
              }
              rowsPerPage={pagination.limit || 10}
              onRowsPerPageChange={(e) => {
                const limit = parseInt(e.target.value, 10) || 10;
                setFilters((prev) => ({
                  ...prev,
                  limit,
                  page: 1,
                }));
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Rows per page:"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default VanTripsList;
